import React from 'react';
import './OrgCreditPanel.css';

/**
 * THE organisation credit panel -- T11
 * (.claude/TODO/Credit-manage-analysis-9-23.md).
 *
 * WHY ONE COMPONENT
 * -----------------
 * Three screens printed the same org pool with three different shapes, three
 * different field names and three different ideas of what a missing value
 * means: the Settings 구독 card, the User Management header chip and the
 * analysis page. They disagreed in production (ticket symptoms 1 and 2), and a
 * customer comparing two of our own screens could not tell which number paid
 * for an analysis. The pool now has exactly ONE renderer; call sites supply
 * data and nothing else.
 *
 * ⚠ A MISSING VALUE IS NEVER 0 -- incident O3 (2026-09-23).
 * `/admin/credits/org-usage` used to be site-admin gated, so for an ORG admin
 * it 403'd, the header chip's `orgCredits` stayed null and it rendered
 * `remaining ?? 0` -- a confident "0 org credits" that actually meant
 * "permission denied". The customer read it as an empty pool and asked why
 * their paid plan was drained. (T10 has since moved that READ to the in-org
 * `access:user_management` gate while the org-GRANT write stayed on the
 * site-admin flags -- see the block at the top of backend/app/api/credits.py
 * for why the two gates deliberately differ. The gate is fixed; the rendering
 * rule below is not conditional on it, because a fetch can still fail.)
 * So `num()` renders a dash for anything that is not a finite number, while a
 * real server-sent 0 still prints 0. An empty pool and an unknown pool are
 * different facts and must never look the same.
 *
 * ⚠ AND A NUMBER WE CANNOT PROVE IS NEVER PRINTED EITHER -- review 2026-09-24.
 * The first version of this panel derived 갱신일 from whatever date-ish field a
 * call site happened to have, and two of those derivations were fabrications:
 * a free/trial org with no membership row got the calendar 1st (see
 * `resolveNextPeriodStart`), and the last month of a fixed-term plan got an
 * anniversary past its own expiry. Both promised a monthly grant that provably
 * never runs. The same class of defect as the O3 zero, in a different column.
 *
 * WHAT `granted` IS -- AND IS NOT
 * ------------------------------
 * `granted` is the tier's `credit_limit`: a per-period CEILING. The amount the
 * anniversary sweep actually WRITES comes from a different source --
 * `product_catalogue.PRODUCTS[...].monthly_credits`, read by
 * `plan_credit_service.monthly_credits_for_tier`. They agree today only
 * because migration 040 set one and migration 042 set the other to the same
 * 200/600, so this panel labels `granted` as a LIMIT and only says "제공"
 * (provided) when a call site passes `grantedThisPeriod`, the real figure. Ask
 * the backend for `granted_this_period` before relabelling it back.
 *
 * T1 (2026-09-23) removed the deep-discount scaling rule -- "a coupon is a
 * discount on PRICE, not a smaller product" -- so there is at least no
 * separate discount-adjusted allowance to reconcile against.
 *
 * THE WORDING (KG이니시스 review, 2026-10-02 --
 * .claude/TODO/Portone-KGinisys-Pricing-fix-10-2.md T2.2). KG classes "크레딧"
 * as 환금성 (cash-equivalent), so this card describes a service with a
 * quantity: 분석 리포트 N건, provided (제공) rather than granted (지급), with no
 * 잔액, 이월 or 갱신. The component, prop and CSS names keep "credit": they are
 * identifiers, not copy (plan §3.3). "이번 달" is said only when the server has
 * proven a monthly grant (`tierGrantsMonthly === true`) -- a trial org's 5
 * reports are a one-time signup grant, and a KG reviewer's test account is a
 * trial org.
 */

// The placeholder for "we do not know", everywhere in this panel. Exported
// because App.js has to hand the same glyph to CreditExhaustedModal, which
// renders whatever it is given: one dash, one definition.
export const DASH = '—';

// Renders a number only when the caller actually has one. null / undefined /
// NaN / a string become the dash; a genuine 0 stays 0. See the O3 note above --
// this single guard is the whole reason the panel exists in one place.
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const num = (v) => (isNum(v) ? v.toLocaleString() : DASH);

/**
 * Plan dates are CALENDAR days, not instants.
 *
 * `new Date('2026-09-14')` is parsed as UTC midnight, so `toLocaleDateString()`
 * prints the 13th for any viewer west of UTC. The API sends `date.isoformat()`,
 * so the Y-M-D is read off the string and rebuilt in local time instead. A
 * timestamp (`...T00:00:00+09:00`) is accepted too -- only the leading date
 * matters, for the same reason.
 */
function parseYmd(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : { y: value.getFullYear(), m: value.getMonth() + 1, d: value.getDate() };
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value == null ? '' : value));
  return m ? { y: +m[1], m: +m[2], d: +m[3] } : null;
}

// Mirrors product_catalogue._add_months: month-end CLAMPS rather than spilling
// into the next month, so a plan bought on the 31st refills on the 28th/29th in
// February and returns to the 31st afterwards. Never skips a month.
function addMonthsClamped({ y, m, d }, n) {
  const total = (y * 12) + (m - 1) + n;
  const ty = Math.floor(total / 12);
  const tm = total % 12;
  const lastDay = new Date(ty, tm + 1, 0).getDate();
  return new Date(ty, tm, Math.min(d, lastDay));
}

/**
 * The 갱신일 the panel prints, or null when a grant cannot be PROVEN to be
 * coming. Only two inputs can prove one:
 *
 *   1. `nextPeriodStart` -- a server-supplied date. Nothing sends it yet; it is
 *      the shape to add when the backend grows a `next_period_start` (strongly
 *      recommended, see the note on branch 2).
 *   2. `planStartDate` + `planEndDate` -- an ACTIVE fixed-term membership.
 *      Credit periods run on the PURCHASE ANNIVERSARY, not the calendar 1st
 *      (ticket Q2, and subscription_service.get_period_start), so the boundary
 *      is the anniversary on or after today, and it is only real while it falls
 *      inside the term: `plan_credit_service.grant_due_for_membership` returns
 *      None the moment `today > end_date` (plan_credit_service.py:122). Without
 *      that clamp the LAST month of every 12-month plan printed a renewal date
 *      the sweep would refuse -- to a customer who is at that exact moment
 *      deciding whether to renew.
 *
 * ⚠ WHAT IS DELIBERATELY NOT A SOURCE (review 2026-09-24): the CURRENT period's
 * start, i.e. `/admin/credits/org-usage.period_start`. It looks authoritative
 * and is not: `get_period_start` falls back to `date(today.year, today.month, 1)`
 * for any org with no active plan membership (subscription_service.py:379), and
 * the client cannot tell that last-resort calendar 1st apart from a real
 * `membership_credit_grants.period_start`. Feeding it here printed "갱신일 <1st
 * of next month>" on every free/trial org -- a recurring grant that does not
 * exist (trial credits are a ONE-TIME 5 written at signup, api/auth.py:351).
 * If a server-computed boundary is wanted on that screen, return
 * `next_period_start` (nullable, and null for orgs with no granting plan) and
 * pass it as branch 1. Do not re-derive it from `period_start`.
 *
 * Exported so the arithmetic is testable without mounting the panel.
 */
export function resolveNextPeriodStart(
  { nextPeriodStart, planStartDate, planEndDate, tierGrantsMonthly = null } = {},
  today = new Date(),
) {
  const explicit = parseYmd(nextPeriodStart);
  if (explicit) return addMonthsClamped(explicit, 0);

  // ⚠ AN ACTIVE MEMBERSHIP TERM DOES NOT MEAN A GRANT IS COMING.
  //
  // Deriving an anniversary from planStartDate assumes the tier actually grants
  // credits every month. Most do not. plan_credit_service.grant_due_for_membership
  // does `allowance = monthly_credits_for_tier(tier); if (!allowance) return None`,
  // and product_catalogue.CATALOGUE carries a monthly allowance for 'starter' and
  // 'pro' ONLY -- trial, enterprise, studio, fgi_starter, fgi_pro and fgi_studio
  // all return nothing. Every one of those can hold an 'active' membership row:
  // GET /admin/payments/tiers lists every row of membership_tiers with no filter,
  // so an admin can assign any of them, and retired studio/fgi plans stay valid
  // to their end_date (see ManageMembershipPage).
  //
  // The panel used to print a confident 갱신일 for all of them -- a date on which
  // nothing will happen, on a billing surface, which is the exact class of
  // confident-wrong-number this component exists to prevent (incident O3).
  //
  // So the derivation is OPT-IN and fails safe: unless the caller can say the
  // tier grants monthly, the answer is a dash. `null` (unknown) is treated as
  // "do not claim", not as "probably yes". The reliable fix is for the server to
  // send `nextPeriodStart` -- it is the only side that can evaluate
  // monthly_credits_for_tier without the tier list drifting into the client --
  // and that branch is above; this one is the fallback.
  if (tierGrantsMonthly !== true) return null;

  const plan = parseYmd(planStartDate);
  if (!plan) return null;

  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  let idx = ((today.getFullYear() - plan.y) * 12) + (today.getMonth() - (plan.m - 1));
  if (idx < 0) idx = 0;
  let candidate = addMonthsClamped(plan, idx);
  // Walks forward, never back: the month arithmetic above is one month out
  // either way depending on the day of the month and the clamp.
  //
  // `<` and not `<=`, so the anniversary DAY ITSELF is still shown as the
  // renewal date. The grant sweep runs at cron(30 4 * * ? *) = 13:30 KST
  // (infra/create-cadence-rules.ps1:95), so for the first ~13.5 hours of every
  // anniversary the credits genuinely have not landed yet and the true answer
  // is "today". `<=` spent that whole morning telling a customer sitting at 0
  // credits that their plan refills in a month.
  while (candidate < startOfToday) {
    idx += 1;
    candidate = addMonthsClamped(plan, idx);
  }

  // Past the end of the term there is no next grant at all -- the downgrade
  // sweep flips the org to free instead. A dash is the honest answer.
  //
  // `>=`, not `>` (T3.6 / D-9, 2026-10-03). product_catalogue.end_date_from is
  // `_add_months(start, 12)`, so the 13th anniversary (period_index 12) falls ON
  // `end_date`, and the sweep used to grant a 13th allowance that lived for
  // about a day. plan_credit_service now refuses any period starting on or
  // after `end_date`, so the end date itself is not a grant date either.
  const end = parseYmd(planEndDate);
  if (end) {
    const endDay = new Date(end.y, end.m - 1, end.d);
    if (candidate >= endDay) return null;
  }
  return candidate;
}

export default function OrgCreditPanel({
  isKo = false,
  remaining = null,
  granted = null,
  // The amount the grant path actually writes for this period, when a call site
  // can get it. Distinct from `granted` on purpose -- see "WHAT `granted` IS".
  grantedThisPeriod = null,
  usedThisPeriod = null,
  nextPeriodStart = null,
  planStartDate = null,
  planEndDate = null,
  // Whether this org's tier grants reports every month. Only `true` permits the
  // anniversary to be DERIVED from planStartDate (null/false render a dash) and
  // only `true` lets the copy say "이번 달" / "this month". See
  // resolveNextPeriodStart for why an active term is not enough on its own.
  tierGrantsMonthly = null,
  unlimited = false,
  unavailableReason = null,
  className = '',
}) {
  const next = resolveNextPeriodStart({
    nextPeriodStart, planStartDate, planEndDate, tierGrantsMonthly,
  });
  // Locale-formatted, matching what the User Management chip already printed.
  const nextLabel = next ? next.toLocaleDateString(isKo ? 'ko-KR' : undefined) : DASH;
  const unlimitedLabel = isKo ? '무제한' : 'Unlimited';
  // "이번 달" / "this month" only for a tier the server says grants monthly.
  // Same fail-safe as the date: null (unknown) is "do not claim", not "yes".
  const monthly = tierGrantsMonthly === true;

  // Two states, because the two call sites this replaces disagreed: the header
  // chip turned red at <= 10 and the analysis page only at 0. Keeping both
  // loses nothing -- exhausted is the one that blocks work, low is a warning.
  const known = !unlimited && isNum(remaining);
  const exhausted = known && remaining <= 0;
  const low = known && remaining > 0 && remaining <= 10;

  // ⚠ `remaining / granted` IS NOT ALWAYS A FRACTION <= 1 (review 2026-09-24).
  // T10a made the anniversary grant additive: `credits_remaining = :allowance +
  // LEAST(credits_purchased, credits_remaining)` (plan_credit_service.py), and
  // the org-grant / credit-pack / bought-report paths add to the pool without
  // touching the allowance. So a Starter org on 200/month legitimately holds
  // 250, and "250 / 200" under the label 이번 달 남은 분석 리포트 reads as a
  // rendering bug -- it generates the support ticket the panel was built to
  // prevent. The allowance is still stated once, in the rows below, plus a
  // sentence saying where the extra came from.
  const overAllowance = known && isNum(granted) && remaining > granted;

  return (
    <section
      className={`org-credit-panel${className ? ` ${className}` : ''}`}
      data-testid="org-credit-panel"
    >
      <div className="ocp-head">
        {/* Mirrored by the d1 nudge email (email_templates/
            d1_first_validation_nudge.html): change the two together. */}
        <h3 className="ocp-title">{isKo ? '분석 리포트 이용 현황' : 'Analysis report usage'}</h3>
        <span className="ocp-chip">{isKo ? '팀 공용' : 'Shared'}</span>
      </div>

      {unavailableReason ? (
        /* O3, stated out loud. A balance we could not read gets a sentence, not
           a number -- and role="status" so a screen reader is told as much. */
        <p className="ocp-unavailable" role="status">{unavailableReason}</p>
      ) : (
        <>
          <div className={`ocp-figure${exhausted ? ' exhausted' : ''}${low ? ' low' : ''}`}>
            <span className="ocp-remaining">{unlimited ? '∞' : num(remaining)}</span>
            {/* No denominator on an unlimited tier: there is no limit to be a
                fraction of, and "∞ / ∞" reads as a bug. None either when the
                pool is above the allowance -- see the overAllowance note. */}
            {!unlimited && !overAllowance && <span className="ocp-of">/ {num(granted)}</span>}
          </div>
          <p className="ocp-figure-label">
            {isKo
              ? (monthly ? '이번 달 남은 분석 리포트' : '남은 분석 리포트')
              : (monthly ? 'Analysis reports left this month' : 'Analysis reports left')}
            {unlimited ? ` (${unlimitedLabel})` : ''}
          </p>

          {/* Describes the legacy extras (a pack holder, an admin goodwill
              grant) WITHOUT promising them: no "이월" -- it contradicts the
              published "미사용분은 다음 달로 이월되지 않음" -- and never "no
              expiry", which is the stored-value promise KG reads as 환금성. */}
          {overAllowance && (
            <p className="ocp-carry" data-testid="ocp-carry">
              {isKo
                ? `월 제공량 ${num(granted)}건 외에 별도로 제공된 분석 리포트가 포함되어 있습니다.`
                : `Includes analysis reports provided separately, on top of this month's ${num(granted)}.`}
            </p>
          )}

          <dl className="ocp-rows">
            <div className="ocp-row">
              {/* "제공" (provided) only when the caller passed the figure the
                  grant path actually writes. Otherwise this is the tier's
                  per-period CEILING and is labelled as one -- "월" only when
                  the tier is proven to grant monthly. The English limit labels
                  are not in the plan: Copy status: pending_ceo (D9). */}
              <dt>
                {isNum(grantedThisPeriod)
                  ? (isKo ? '이번 달 제공' : 'Provided this month')
                  : (isKo
                    ? (monthly ? '월 분석 리포트 한도' : '분석 리포트 한도')
                    : (monthly ? 'Monthly report limit' : 'Report limit'))}
              </dt>
              <dd>
                {isNum(grantedThisPeriod)
                  ? num(grantedThisPeriod)
                  : (unlimited ? unlimitedLabel : num(granted))}
              </dd>
            </div>
            <div className="ocp-row">
              <dt>{isKo ? '이번 달 사용' : 'Used this month'}</dt>
              {/* `credits_used` on the organisations row is a LIFETIME counter
                  (T5). Call sites must pass the PER-PERIOD figure, which is
                  null when it is not derivable -- hence a dash, not a 0. */}
              <dd data-testid="ocp-used">{num(usedThisPeriod)}</dd>
            </div>
            <div className="ocp-row">
              {/* Was 갱신일 / "Renews on", which reads as auto-renewal on a
                  one-time 12-month product. The data-testid keeps its name. */}
              <dt>{isKo ? '다음 제공일' : 'Next reports on'}</dt>
              {/* A dash here means "no grant we can prove is coming": no plan,
                  or the final month of a term. See resolveNextPeriodStart. */}
              <dd data-testid="ocp-renews">{nextLabel}</dd>
            </div>
          </dl>
        </>
      )}

      <p className="ocp-note">
        {isKo
          ? '조직 전체가 함께 사용합니다. 멤버별 배정은 없습니다.'
          : 'Your whole organization shares this pool. There is no per-member allocation.'}
      </p>
    </section>
  );
}
